import { apiClient } from "../client";
import type { LaravelPaginator } from "./catalog";

/** The live envelope uses `success`, not the `status` field `Envelope<T>`
 * (./catalog) expects, so it's modeled separately here. */
export interface GallerySuccessEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: { current_page: number; last_page: number; per_page: number; total: number };
}

/**
 * A /gallery record is an ALBUM (verified against a live response sample):
 * a cover image plus a bundle of `images` (and, per `total_videos`,
 * `videos` — not present in the sample but implied by that count). Each
 * media entry may come back as a bare URL string or an object carrying one.
 */
export type GalleryMediaEntry = string | { url?: string; video_url?: string; image_url?: string; path?: string };

export interface GalleryItem {
  id: number | string;
  title?: string | null;
  name?: string | null;
  description?: string | null;
  caption?: string | null;
  cover_image?: string | null;
  thumbnail?: string | null;
  images?: GalleryMediaEntry[];
  videos?: GalleryMediaEntry[];
  total_images?: number;
  total_videos?: number;
  total_media?: number;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface GalleryListParams {
  page?: number;
  per_page?: number;
  category?: string;
}

export type GalleryListResponse =
  | GallerySuccessEnvelope<GalleryItem[]>
  | LaravelPaginator<GalleryItem>
  | GalleryItem[];

export type GalleryItemResponse = GallerySuccessEnvelope<GalleryItem> | GalleryItem;

export const gallery = {
  /** GET /gallery — paginated list of gallery albums. */
  list: (params: GalleryListParams = {}) =>
    apiClient.get<GalleryListResponse>("/gallery", { params: { ...params } }),

  /** GET /gallery/{id} — single album detail (used to enrich the album viewer). */
  get: (id: number | string) =>
    apiClient.get<GalleryItemResponse>(`/gallery/${id}`),
};
